// src/oura/overview/model.ts
//
// The staff Overview as data: the page sentence, what the AI tile says, what
// to do next and what needs attention. Pure functions over payloads the screen
// already holds. Nothing here fetches, and nothing here invents a number: a
// fact the payload does not carry is left out, never zero-filled.

import type { AeoGrounding, AeoLatestAttempt, AeoLevelCap } from "@service/aeo";
import type { Deliverable, DeliveryPayload } from "@service/aeo/delivery";
import { dayLabel, todayRank } from "@service/aeo/delivery";
import { workingDaysBetween } from "@service/aeo/clientDocsRules";
import type { Pack, PackItem, Thread, TimelineConflict, TimelinePayload } from "@service/aeo/clientReview";
import { requesterName, type ClientRequest } from "../components/client/changeRequests";
import type { Recommendation } from "../audit";
import type { DataConnector } from "../connectors";
import type { GscState } from "../gscState";
import type { Places, PlaceSources } from "../places";
import { blockLabel, type BusinessTracker, type TaskStatus, type TrackerBlock, type TrackerTask } from "../tracker";
import { currentBlockOf, daysLate, todayKey, whoMovesNext, type MovesNextContext, type PlacedTask } from "../components/fixes/model";
import { isFallbackInstrument, isMixedInstrument } from "../components/aeo/InstrumentBadge";
import { countedUnitLabel, dayDate } from "../lib/oura";
import { ouraHref, type View } from "../nav";
import { onboardingSteps } from "../onboarding/derive";
import type { Onboarding } from "@service/aeo/onboarding";
import type { VisRival } from "../visibility/model";

/* ------------------------------------------------------------------------- *
 * Words and dates
 * ------------------------------------------------------------------------- */

/** "1 day", "3 days". */
export function count(n: number, noun: string, nouns = `${noun}s`): string {
    return `${n} ${n === 1 ? noun : nouns}`;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}/;

/**
 * The calendar day a value falls on here. A date-only value is already one; a
 * timestamp (`next_auto_scan_at`) is placed in local time, the way `dayDate`
 * prints it, so the row's date and its position in the list agree.
 */
function localDay(value: string): string | null {
    if (value.length <= 10) return DATE_ONLY.test(value) ? value : null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : todayKey(d);
}

/** Whole calendar days from `from` to `to`, both `YYYY-MM-DD`. */
function daysFrom(from: string, to: string): number {
    const utc = (s: string) => {
        const [y, m, d] = s.slice(0, 10).split("-").map(Number);
        return Date.UTC(y, m - 1, d);
    };
    return Math.round((utc(to) - utc(from)) / 86_400_000);
}

/**
 * "The plan and the client's page could not be read": the sources that
 * failed, named, so the reader knows which part of a list may be short. Null
 * when every source answered.
 */
export function couldNotRead(sources: string[]): string | null {
    if (!sources.length) return null;
    const last = sources[sources.length - 1];
    const list = sources.length === 1 ? last : `${sources.slice(0, -1).join(", ")} and ${last}`;
    return `${list.charAt(0).toUpperCase()}${list.slice(1)} could not be read`;
}

/**
 * The note under a column with no rows in it.
 *
 * A column is empty for two different reasons, and they must not read alike: a
 * source answered and had nothing, or a source did not answer at all. The
 * second one says so instead of making the claim (Jack, #4011).
 */
export function emptyColumnNote(nothing: string, unread: string | null): string {
    return unread ? `${unread}, so this may not be everything.` : nothing;
}

/**
 * The first sentence of a backend explanation. Instrument notes and error
 * details run to several hundred characters; a row states the fact and the
 * screen it links to carries the rest.
 */
function firstSentence(text: string | null | undefined): string | null {
    const t = (text ?? "").trim();
    if (!t) return null;
    return t.split(/(?<=[.!?])\s/)[0].trim();
}

/* ------------------------------------------------------------------------- *
 * Links
 * ------------------------------------------------------------------------- */

/** Where a row goes: a staff tab of this business, optionally anchored on one thing in it. */
export interface Link {
    view: View;
    /** Fixes anchored on one task (`?task=`). */
    task?: string;
    /** Fixes anchored on the task behind a recommendation (`?rec=` plus the title as the hint). */
    rec?: { id: string; title: string };
    /** Client opened on one ask (`?item=`). */
    item?: string;
    /** Client opened on one thing the client started, a change or a call (`?request=`). */
    request?: string;
    /**
     * Client opened on one card: Requests, for several requests at once
     * (`#requests`), or Documents, where the client is asked now (`#documents`).
     */
    card?: "requests" | "documents";
    /** Client opened at the weekly note composer (`?compose=note`). */
    compose?: "note";
    /** Delivery opened on one deliverable's drawer (`?d=`). */
    deliverable?: string;
}

/**
 * The URL for a link. Rows are real links, so they open in a new tab and
 * survive a refresh and a share.
 *
 * `?task=` is the canonical form the router's `onOpenTrackerTaskById` writes.
 * A recommendation carries both `rec` and `task-hint` (#3397): `rec` resolves
 * on live data, where its `rec_<check key>` id joins the task's `source.ref`,
 * and the hint serves fixtures and the message Fixes shows when no step covers
 * it. Fixes rewrites both to `?task=` once it resolves one.
 */
export function staffHref(businessId: string, link: Link): string {
    const base = ouraHref(link.view, { role: "oura-staff", businessId });
    if (link.deliverable) return `${base}?d=${encodeURIComponent(link.deliverable)}`;
    if (link.task) return `${base}?task=${encodeURIComponent(link.task)}`;
    if (link.rec) return `${base}?rec=${encodeURIComponent(link.rec.id)}&task-hint=${encodeURIComponent(link.rec.title)}`;
    if (link.item) return `${base}?item=${encodeURIComponent(link.item)}`;
    if (link.request) return `${base}?request=${encodeURIComponent(link.request)}`;
    if (link.compose) return `${base}?compose=${link.compose}`;
    if (link.card) return `${base}#${link.card}`;
    return base;
}

/* ------------------------------------------------------------------------- *
 * The page sentence
 * ------------------------------------------------------------------------- */

/** The 1 to 5 rating and what holds it down (`results.reportMetrics`). */
export interface Rating {
    level: number | null;
    cappedBy: AeoLevelCap | null;
}

/**
 * The backend's verdict without its subject. The sentence already says what is
 * being judged ("Named in 23 of 50"), so "Weak AI visibility, high upside"
 * reads "Weak, high upside". The band itself is the backend's, never re-derived.
 */
function verdictWords(verdict: string | null | undefined): string | null {
    const words = (verdict ?? "")
        .replace(/\s*(?:in\s+)?AI visibility\b/i, "")
        .replace(/[.\s]+$/, "")
        .trim();
    return words || null;
}

/**
 * One verdict per scan. With a Visibility model it is that screen's band, in
 * the words its tile uses ("Low visibility"); only a scan with no model falls
 * back to the audit's verdict.
 */
export function scanVerdict(vis: { verdict: string | null } | null, auditVerdict: string | null | undefined): string | null {
    if (vis) return vis.verdict ? `${vis.verdict} visibility` : null;
    return verdictWords(auditVerdict);
}

/**
 * Why the rating is held down, in words.
 *
 * The claim comes from `levelCappedBy`, the backend's own reason, and only the
 * count beside it comes from `citationShare`. They are two measures (the cap
 * counts answers that cited anything, the share counts the set in its own
 * unit), so the count never decides whether there is a cap.
 */
function capClause(cap: AeoLevelCap | null, sources: PlaceSources): string | null {
    if (cap === "site_health") return "Site health is below 50";
    if (cap !== "rented_visibility") return null;
    const { ownAnswers: own, citedAnswers: of } = sources;
    if (own === null || of === null) return "AI mentions it through directories, not its own website";
    const unit = countedUnitLabel(of, sources.shareBasis);
    return own === 0 ? `none of the ${of} ${unit} cite its own site` : `only ${own} of ${of} ${unit} cite its own site`;
}

/**
 * "Named in 23 of 50. Only 3 of 23 answers cite its own site." Said once, in
 * two short facts (2 Nov 2026): the AI count and, when the rating is capped,
 * what holds it down. Each clause drops out when its fact is absent; null
 * when there is nothing to say at all.
 *
 * The verdict and the 1-to-5 rating moved off the line (it read as four
 * claims in a row); `overviewDetail` keeps them, shown on hover.
 */
export function overviewSentence(
    { named, of }: { named: number; of: number },
    sources: PlaceSources,
    rating: Rating,
): string | null {
    const parts: string[] = [];
    if (of > 0) parts.push(`Named in ${named} of ${of}.`);
    const cap = rating.level !== null ? capClause(rating.cappedBy, sources) : null;
    if (cap) parts.push(`${cap.charAt(0).toUpperCase()}${cap.slice(1)}.`);
    return parts.length ? parts.join(" ") : null;
}

/**
 * The rest of the old sentence, for the hover: "Low visibility. Overall rating
 * 3 of 5, capped." `verdict` is `scanVerdict`'s.
 *
 * "OVERALL" earns its word. The rating is the backend's 1-to-5 level over the
 * AREA SCORES (`maturity_from_areas`), not a rating of the visibility count
 * before it, so "Rating 3 of 5" beside "Named in 0 of 50" read as a
 * contradiction and staff asked what the 3 was for (#3989).
 */
export function overviewDetail(verdict: string | null, rating: Rating): string | null {
    const parts: string[] = [];
    if (verdict) parts.push(`${verdict}.`);
    if (rating.level !== null) parts.push(`Overall rating ${rating.level} of 5${rating.cappedBy ? ", capped" : ""}.`);
    return parts.length ? parts.join(" ") : null;
}

/* ------------------------------------------------------------------------- *
 * The AI assistants tile
 * ------------------------------------------------------------------------- */

/** The rival that takes the most questions: named where the business was not. */
export interface TopRival {
    name: string;
    count: number;
}

/**
 * The leading rival from Visibility's own model, so the tile names the rival
 * the Competitors tab ranks first, with the same count. Null when no rival
 * took a question.
 */
export function topRival(rivals: VisRival[]): TopRival | null {
    const lead = rivals.find((r) => r.takes > 0);
    return lead ? { name: lead.name, count: lead.takes } : null;
}

/* ------------------------------------------------------------------------- *
 * The plan as Fixes left it
 * ------------------------------------------------------------------------- */

/** This session's edits on Fixes, as `trackerState` resolves them. */
export interface SessionEdits {
    statusOf: (id: string, base: TaskStatus) => TaskStatus;
    weekOf: (id: string, base: number) => number;
    isRemoved: (id: string) => boolean;
}

/**
 * A sample business's plan with the statuses, week moves and removals made on
 * Fixes applied, so the Overview reads the plan Fixes shows. A live plan does
 * not need this: its edits are saved and the Overview reads them back.
 *
 * A task moved to a block the plan does not have joins it as a bare block,
 * the way Fixes draws it.
 */
export function withSessionEdits(tracker: BusinessTracker, edits: SessionEdits): BusinessTracker {
    const placed = tracker.weeks.flatMap((w) =>
        w.tasks
            .filter((t) => !edits.isRemoved(t.id))
            .map((t) => ({ task: { ...t, status: edits.statusOf(t.id, t.status) }, week: edits.weekOf(t.id, w.week) })),
    );
    const blocks = new Map(tracker.weeks.map((w) => [w.week as number, w]));
    for (const { week } of placed) {
        if (!blocks.has(week)) {
            blocks.set(week, { week: week as TrackerBlock, title: blockLabel(week), goal: "", deliverable: "", dates: "", tasks: [] });
        }
    }
    const weeks = Array.from(blocks.values())
        .sort((a, b) => a.week - b.week)
        .map((w) => ({ ...w, tasks: placed.filter((p) => p.week === w.week).map((p) => p.task) }));
    return { ...tracker, weeks };
}

/* ------------------------------------------------------------------------- *
 * What to do next
 * ------------------------------------------------------------------------- */

/** The one status a row carries on the right. */
export type NextStatus =
    | { kind: "late"; days: number }
    /** A fix that came back: the scan found it again (Delivery). */
    | { kind: "came_back" }
    /**
     * A live task the client owns with no question of ours joined to it. It
     * makes no claim about whether they were asked: that is in the Google
     * Docs now, which Oura cannot match to a task yet (YW, 29 Sep 2026).
     */
    | { kind: "client_turn" }
    /** Something the client started (a change, a call) that is ours until Done: "answered" once we replied. */
    | { kind: "request"; answered: boolean }
    | { kind: "in_progress"; due: string | null }
    | { kind: "waiting" }
    | { kind: "blocked" }
    /** `due`: when it is due, where Delivery has a date (#4737). Absent elsewhere. */
    | { kind: "not_started"; due?: string | null }
    /** A recommendation with no plan behind it yet; `outside`: no deliverable on the contract covers it. */
    | { kind: "unplanned"; outside?: boolean };

export interface NextRow {
    key: string;
    title: string;
    status: NextStatus;
    /** The row is an ask the client can see, or a task one is joined to. */
    asked: boolean;
    link: Link;
    /** The deliverable's code, drawn as its chip (Delivery rows). */
    code?: string;
}

/** Rows each section of What to do next shows before "Show N more" (D11). */
export const NEXT_ROWS_SHOWN = 6;

export interface NextWork {
    us: NextRow[];
    client: NextRow[];
    /** Every open task on the plan: the count the card's head link carries. */
    open: number;
}

/**
 * Late first, then what the client started, then the client's own tasks, then
 * what is moving, then what waits, then what has not begun; plan order after
 * that.
 */
const STATUS_RANK: Record<NextStatus["kind"], number> = {
    came_back: 0,
    late: 0,
    request: 1,
    client_turn: 2,
    in_progress: 3,
    waiting: 4,
    blocked: 5,
    not_started: 6,
    unplanned: 7,
};

/** Asks the client can see and has not answered. */
export function asksWaitingOnClient(packs: Pack[]): PackItem[] {
    return packs
        .filter((p) => p.status !== "draft")
        .flatMap((p) => p.items)
        .filter((i) => i.waitingOn === "client");
}

/**
 * How late an ask is. The server's `lateDays` when it sends one; until then
 * only an ask joined to a task with a due date can be called late, and any
 * other ask makes no claim about a deadline.
 */
function askLateDays(ask: PackItem, task: TrackerTask | undefined, today: string): number {
    if (typeof ask.lateDays === "number") return Math.max(0, ask.lateDays);
    return task ? daysLate(task, today) : 0;
}

/**
 * The plan split by who moves next (D8, D11), by the rule Fixes groups with.
 *
 * In scope: the block the plan is in now (the one Fixes opens on), anything
 * late, and anything the client has been asked about. A row an ask stands
 * behind opens that ask on Client. A client task with no ask is the client's
 * turn and opens Client on its Documents card, where the client is asked now;
 * it is never called late, because Oura cannot see whether a doc asked for
 * it. An ask that joins no open task is a row of its own: it is still
 * something the client holds up.
 *
 * ONBOARDING COUNTS TOO. A client still being set up has no plan tasks and no
 * asks, so both sides read "nothing" — while the Onboarding tab says the
 * client has to give access and the Businesses list says "Waiting for access"
 * (#4006). Overview is the first page staff open, so for a new client it was
 * hiding the only thing that was blocking. The onboarding steps are the
 * client's open work too, and they are split by the same question this card
 * asks: who moves next.
 */
export function whatNext(
    tracker: BusinessTracker | null,
    asks: PackItem[],
    today: string,
    requests: readonly ClientRequest[] = [],
    onboarding: Onboarding | null = null,
): NextWork {
    const weeks = tracker?.weeks ?? [];
    const open = weeks.flatMap((w) =>
        w.tasks.filter((task) => task.status !== "done").map((task): PlacedTask => ({ ...task, block: w.week })),
    );
    const askFor = new Map<string, PackItem>();
    for (const a of asks) if (!askFor.has(a.taskId)) askFor.set(a.taskId, a);
    const ctx: MovesNextContext = { waitingOnClient: new Set(askFor.keys()), today, currentBlock: currentBlockOf(weeks) };

    // Each row keeps its plan position, the tie-break after status.
    const us: Array<{ row: NextRow; order: number }> = [];
    const client: Array<{ row: NextRow; order: number }> = [];
    open.forEach((task, order) => {
        const ask = askFor.get(task.id);
        const clientSide = whoMovesNext(task, ctx) === "needs_client";
        const lateDays = ask ? askLateDays(ask, task, today) : daysLate(task, today);
        if (task.block !== ctx.currentBlock && lateDays === 0 && !ask) return;
        const status: NextStatus =
            clientSide && !ask ? { kind: "client_turn" }
            : lateDays > 0 ? { kind: "late", days: lateDays }
            : ask ? { kind: "waiting" }
            : task.status === "in_progress" ? { kind: "in_progress", due: task.targetDate ?? null }
            : task.status === "blocked" ? { kind: "blocked" }
            : { kind: "not_started" };
        const link: Link = ask
            ? { view: "reviews", item: ask.id }
            : clientSide
                ? { view: "reviews", card: "documents" }
                : { view: "todo", task: task.id };
        const row: NextRow = { key: task.id, title: task.action, status, asked: !!ask, link };
        (clientSide ? client : us).push({ row, order });
    });

    const openIds = new Set(open.map((task) => task.id));
    asks
        .filter((a) => !openIds.has(a.taskId))
        .forEach((a, i) => {
            const days = askLateDays(a, undefined, today);
            client.push({
                row: {
                    key: `ask-${a.id}`,
                    title: a.title,
                    status: days > 0 ? { kind: "late", days } : { kind: "waiting" },
                    asked: true,
                    link: { view: "reviews", item: a.id },
                },
                order: open.length + i,
            });
        });

    // What the client started and we owe (Ask for a change, a call), until it is
    // Done: ours, whether or not a plan exists. Each opens on the Requests card.
    requests.forEach((r, i) => {
        us.push({ row: requestRow(r), order: open.length + asks.length + i });
    });

    // The onboarding steps still open, each on the side its own state names.
    // `oura` is Oura working by itself and `later` cannot start yet, so
    // neither is a thing a person does next; only `ours` and `theirs` are.
    onboardingRows(onboarding).forEach(({ row, mine }, i) => {
        const order = open.length + asks.length + requests.length + i;
        (mine ? us : client).push({ row, order });
    });

    const sorted = (rows: Array<{ row: NextRow; order: number }>): NextRow[] =>
        rows
            .sort((a, b) => STATUS_RANK[a.row.status.kind] - STATUS_RANK[b.row.status.kind] || a.order - b.order)
            .map(({ row }) => row);
    return { us: sorted(us), client: sorted(client), open: open.length };
}

/**
 * The onboarding steps that are somebody's to move, as rows for this card.
 *
 * `state` is the module's own answer to who moves next, so it is what splits
 * them: `ours` is staff, `theirs` is the client. `done` is finished, `oura` is
 * Oura working by itself, and `later` cannot start yet — none of those is a
 * next step for a person, and putting them here would refill the card with
 * things nobody can act on.
 *
 * Every row opens Onboarding, which is where each of them is actually done.
 *
 * A finished onboarding contributes nothing. `completedAt` is the record's own
 * word for that, and once it is set the steps are history: an unrecorded
 * payment on a client who has been live for months is not what Overview should
 * put in front of staff as the next thing to do.
 */
function onboardingRows(o: Onboarding | null): Array<{ row: NextRow; mine: boolean }> {
    if (!o || o.completedAt) return [];
    return onboardingSteps(o)
        .filter((step) => step.state === "ours" || step.state === "theirs")
        .map((step) => ({
            mine: step.state === "ours",
            row: {
                key: `onboarding:${step.key}`,
                title: step.title,
                // Late is the step's own word; without a day count this card
                // must not invent one, so a late step reads as waiting rather
                // than claiming a number it does not have.
                status: step.late ? { kind: "waiting" } : step.state === "theirs" ? { kind: "client_turn" } : { kind: "not_started" },
                asked: step.state === "theirs",
                link: { view: "onboarding" as View },
            },
        }));
}

/** One thing the client started, as a row: who asked for what, opening that request. */
function requestRow(r: ClientRequest): NextRow {
    const name = r.kind === "change" ? requesterName(r.change) : requesterName(r.call);
    return {
        key: `request-${r.id}`,
        // A change by its own words, so 3 from one person read apart.
        title: r.kind === "change" ? requestGist(r) : `${name} asked for a call`,
        status: { kind: "request", answered: r.kind === "change" && r.change.status === "answered" },
        asked: false,
        link: { view: "reviews", request: r.id },
    };
}

/** "Mei Ling: “Sunday brunch on the menu page”", "Siew Ling: a call, This week, mornings": a request in a few words. */
function requestGist(r: ClientRequest, max = 60): string {
    if (r.kind === "call") return [`${requesterName(r.call)}: a call`, r.call.preference.trim() || null].filter(Boolean).join(", ");
    const note = r.change.note.replace(/\s+/g, " ").trim();
    const short = note.length > max ? `${note.slice(0, max - 1).replace(/[\s,.;:]+$/, "")}…` : note;
    return `${requesterName(r.change)}: \u201c${short}\u201d`;
}

/**
 * With no plan yet, the top of what the scan recommends. A plan is built on
 * Fixes, never here: reading the full tracker is what creates one.
 */
export function recommendationRows(recs: Recommendation[], limit = 3, delivery = false): NextRow[] {
    return recs
        .filter((r) => r.priority === "high" && r.workState !== "done")
        .slice(0, limit)
        .map((r): NextRow => {
            // A card a deliverable covers is part of it: its chip, and its drawer (H13).
            if (r.code && r.deliverableKey) {
                return {
                    key: r.id,
                    title: r.title,
                    status: r.workState === "in_progress" ? { kind: "in_progress", due: null } : { kind: "not_started" },
                    asked: false,
                    link: { view: "delivery", deliverable: r.deliverableKey },
                    code: r.code,
                };
            }
            // With Delivery on, work outside the contract is added from the Site screen.
            return {
                key: r.id,
                title: r.title,
                status: { kind: "unplanned", outside: delivery },
                asked: false,
                link: delivery ? { view: "setup" } : { view: "todo", rec: { id: r.id, title: r.title } },
            };
        });
}

export const isLate = (row: NextRow): boolean => row.status.kind === "late";

/* ------------------------------------------------------------------------- *
 * Needs attention
 * ------------------------------------------------------------------------- */

/** Amber: a problem or a late ask. Grey: something not set up. Blue: a date coming up. */
export type AttentionTone = "amber" | "gray" | "blue";

export interface AttentionRow {
    key: string;
    tone: AttentionTone;
    title: string;
    sub: string | null;
    link: Link;
    /** Dated rows only, `YYYY-MM-DD`, for their order. */
    day?: string;
}

/**
 * Client replies a person has to answer: the Client tab's inbox rule. An item
 * whose ball is in our court, or an open thread whose last word is the
 * client's own message.
 */
export function repliesWaitingOnUs(packs: Pack[], threads: Thread[]): Array<{ thread: Thread; item: PackItem }> {
    const items = new Map(packs.flatMap((p) => p.items.map((i) => [i.id, i] as const)));
    return threads.flatMap((thread) => {
        const item = items.get(thread.itemId);
        if (!item) return [];
        const last = thread.messages[thread.messages.length - 1];
        const clientWrote = thread.messages.some(
            (m) => m.authorKind === "client" && (m.kind === "message" || m.kind === "suggested_edit"),
        );
        const waits = item.waitingOn === "staff" || (thread.status === "open" && clientWrote && last?.authorKind === "client");
        return waits ? [{ thread, item }] : [];
    });
}

export interface AttentionInput {
    today: string;
    next: NextWork;
    replies: Array<{ item: PackItem }>;
    grounding: AeoGrounding | null | undefined;
    places: Places;
    latestAttempt: AeoLatestAttempt | null | undefined;
    /** Undefined until the connector catalog arrives, and on a fixture. */
    gsc: GscState | undefined;
    /** Undefined: not known yet. Null: the catalog arrived without a GA4 entry. */
    ga4: DataConnector | null | undefined;
    hasTraffic: boolean;
    connectors: DataConnector[] | undefined;
    timeline: TimelinePayload | null;
    tracker: BusinessTracker | null;
    nextScanAt: string | null | undefined;
}

/** How far ahead a task date counts as coming up. */
const DATED_WINDOW_DAYS = 14;
const DATED_TASKS_MAX = 3;

/** The Client tab, opened on the one item a row is about, or on its list when there are several. */
function clientLink(itemIds: Array<string | null>): Link {
    const [only] = itemIds;
    return itemIds.length === 1 && only ? { view: "reviews", item: only } : { view: "reviews" };
}

function instrumentRow(g: AeoGrounding | null | undefined): AttentionRow | null {
    if (!g) return null;
    const partly = isMixedInstrument(g);
    if (!partly && !isFallbackInstrument(g)) return null;
    return {
        key: "instrument",
        tone: "amber",
        title: partly ? "ChatGPT partly asked on the API" : "ChatGPT asked on the API, not the app",
        sub: firstSentence(g.instrumentNote)
            ?? "API answers read lower than the ChatGPT app a customer uses.",
        link: { view: "questions" },
    };
}

function oneEngineRow(places: Places): AttentionRow | null {
    const { engines, of } = places.assistants;
    const measured = engines.filter((e) => !e.skipped);
    if (of === 0 || measured.length !== 1) return null;
    const skipped = engines.find((e) => e.skipped);
    return {
        key: "one-engine",
        tone: "amber",
        title: "Measured on 1 engine only",
        sub: skipped ? `${skipped.label}: ${skipped.skipped}` : `The verdict rests on ${measured[0].label} alone.`,
        link: { view: "questions" },
    };
}

function attemptRow(attempt: AeoLatestAttempt | null | undefined): AttentionRow | null {
    if (!attempt) return null;
    const why = firstSentence(attempt.error_detail) ?? attempt.error_code?.replace(/_/g, " ") ?? null;
    const when = attempt.captured_at ? dayDate(attempt.captured_at) : null;
    return {
        key: "attempt",
        tone: "amber",
        title: attempt.status === "failed"
            ? "Last re-scan failed"
            : attempt.status === "cancelled"
                ? "Last re-scan was cancelled"
                : "Last re-scan gave no usable result",
        sub: [when, why].filter(Boolean).join(": ") || null,
        link: { view: "history" },
    };
}

/**
 * Connection problems, for the connectors Oura reads: Search Console, Google
 * Analytics and the website platform. Google Business Profile is not one of
 * them (#3801) and never gets a row.
 */
function connectorRows(input: AttentionInput): AttentionRow[] {
    const rows: AttentionRow[] = [];
    const { gsc, ga4 } = input;
    if (gsc?.kind === "not_connected") {
        rows.push({ key: "gsc", tone: "gray", title: "Search Console not connected", sub: "Connect it to read clicks and average position.", link: { view: "connectors" } });
    } else if (gsc?.tone === "warn") {
        rows.push({ key: "gsc", tone: "amber", title: `Search Console: ${gsc.label}`, sub: gsc.detail, link: { view: "connectors" } });
    }
    if (ga4 === null || ga4?.status === "not_connected") {
        rows.push({ key: "ga4", tone: "gray", title: "Google Analytics not connected", sub: "Connect it to read sessions on Search performance.", link: { view: "connectors" } });
    } else if (ga4?.status === "action_needed") {
        rows.push({ key: "ga4", tone: "amber", title: "Google Analytics needs attention", sub: ga4.detail || "The connection stopped returning data.", link: { view: "connectors" } });
    } else if (ga4?.status === "connected" && !input.hasTraffic) {
        // Connected, and GA4 answered with no sessions: most often a property
        // that is not this client's (ourteam-backend#4947).
        rows.push({ key: "ga4", tone: "amber", title: "Google Analytics returned no sessions", sub: "Check that the connected property is this client's site.", link: { view: "connectors" } });
    }
    for (const c of input.connectors ?? []) {
        if (c.category === "cms" && c.status === "action_needed") {
            rows.push({ key: `cms-${c.key}`, tone: "amber", title: `${c.label} needs attention`, sub: c.detail || null, link: { view: "connectors" } });
        }
    }
    return rows;
}

/**
 * A conflict in words a person reads, for the codes the timeline engine
 * raises (`aeo_timeline.py`). Its own message names spec fields
 * ("priority_keywords has 2 entries"), so it is kept for a code this list
 * does not know yet.
 */
function conflictSentence(conflict: TimelineConflict, timeline: TimelinePayload): string {
    const schedule = timeline.computed;
    const months = schedule?.months ?? [];
    const scheduled = months.length ? months[months.length - 1].pages_cumulative : null;
    switch (conflict.code) {
        case "priority_keywords_incomplete":
            if (schedule) return `${count(schedule.priority_keywords.length, "priority keyword")} recorded. The catalogue asks for 5 to 10.`;
            break;
        case "term_differs_from_catalogue":
            if (schedule) return `The contract runs ${count(schedule.term_months, "month")}, not the catalogue's term. Check the deviation is recorded.`;
            break;
        case "unit_total_differs_from_catalogue":
            if (schedule) return `The contract sells ${count(schedule.total_units, "page unit")}, not the catalogue's number. Check the deviation is recorded.`;
            break;
        case "units_not_fully_scheduled":
            if (schedule && scheduled !== null) return `${scheduled} of ${count(schedule.total_units, "page unit")} are scheduled across the term.`;
            break;
        case "signature_incomplete":
            return "A signature question is missing. Record it on the plan, under What was sold.";
        case "outlets_missing":
            return "No outlets recorded. The outlet band depends on them.";
    }
    return conflict.message;
}

function timelineRows(timeline: TimelinePayload | null): AttentionRow[] {
    if (!timeline) return [];
    const rows: AttentionRow[] = [];
    if (timeline.published === null) {
        rows.push({ key: "timeline-unpublished", tone: "amber", title: "Plan never published", sub: "The client sees no dates yet.", link: { view: "timeline" } });
    } else if (timeline.changePending) {
        rows.push({ key: "timeline-pending", tone: "amber", title: "Plan change not published", sub: "The client still sees the dates last published.", link: { view: "timeline" } });
    }
    if (timeline.conflicts.length) {
        rows.push({
            key: "timeline-conflicts",
            tone: "amber",
            title: `${count(timeline.conflicts.length, "timeline conflict")} with the contract`,
            sub: conflictSentence(timeline.conflicts[0], timeline),
            link: { view: "timeline" },
        });
    }
    return rows;
}

/**
 * Task dates in the next two weeks. The client's three touchpoints
 * (`access`, `review`, `handover`) win a place first: those are the dates the
 * client plans around. A task already raised as a problem above is not
 * listed again by its date.
 */
function datedTaskRows(tracker: BusinessTracker | null, today: string, raised: ReadonlySet<string>): AttentionRow[] {
    const upcoming = (tracker?.weeks ?? [])
        .flatMap((w) => w.tasks)
        .filter((t) => t.status !== "done" && !raised.has(t.id) && !!t.targetDate && DATE_ONLY.test(t.targetDate))
        .map((t) => ({ t, day: (t.targetDate as string).slice(0, 10) }))
        .filter(({ day }) => {
            const ahead = daysFrom(today, day);
            return ahead >= 0 && ahead <= DATED_WINDOW_DAYS;
        })
        .sort((a, b) => Number(!!b.t.touchpoint) - Number(!!a.t.touchpoint) || a.day.localeCompare(b.day))
        .slice(0, DATED_TASKS_MAX);
    return upcoming.map(({ t, day }) => ({
        key: `due-${t.id}`,
        tone: "blue",
        title: dayDate(day) ?? day,
        sub: t.owner === "client" ? `From the client: ${t.action}` : t.action,
        link: { view: "todo", task: t.id },
        day,
    }));
}

/**
 * The next monthly report and the next scheduled scan. One row when they fall
 * on the same day, the way the plan usually lands them.
 */
function scheduleRows(timeline: TimelinePayload | null, nextScanAt: string | null | undefined, today: string): AttentionRow[] {
    const months = (timeline?.published?.schedule ?? timeline?.computed)?.months ?? [];
    const report = months
        .map((m) => localDay(m.report_date))
        .filter((d): d is string => !!d && d >= today)
        .sort()[0];
    const scan = nextScanAt ? localDay(nextScanAt) : null;
    if (report && report === scan) {
        return [{ key: "scan-report", tone: "blue", title: dayDate(report) ?? report, sub: "Next scan and the monthly report", link: { view: "timeline" }, day: report }];
    }
    const rows: AttentionRow[] = [];
    if (report) rows.push({ key: "report", tone: "blue", title: dayDate(report) ?? report, sub: "Monthly report", link: { view: "timeline" }, day: report });
    if (scan && nextScanAt) rows.push({ key: "scan", tone: "blue", title: dayDate(nextScanAt) ?? scan, sub: "Next scan", link: { view: "history" }, day: scan });
    return rows;
}

/**
 * Everything that needs a person, problems first, then set-up, then dates in
 * the order they come. Each row links to where it is dealt with.
 */
export function attentionRows(input: AttentionInput): AttentionRow[] {
    // What to do next, beside this card, already lists every late question to
    // the client and every request that needs us, so they are not said twice
    // (Jack, 22 Oct 2026: the two cards read as one list repeated). This card
    // keeps what that list has no row for. `raised` still keeps a dated task a
    // late question covers from coming back as a date.
    const raised: ReadonlySet<string> = new Set(input.next.client.filter((r) => r.status.kind === "late").map((r) => r.key));

    const replies: AttentionRow[] = input.replies.length
        ? [{
            key: "replies",
            tone: "amber",
            title: `${count(input.replies.length, "client reply", "client replies")} to answer`,
            sub: input.replies.map((r) => r.item.title).join(", "),
            link: clientLink(input.replies.map((r) => r.item.id)),
        }]
        : [];

    const problems = [
        ...replies,
        instrumentRow(input.grounding),
        oneEngineRow(input.places),
        attemptRow(input.latestAttempt),
        ...connectorRows(input),
        ...timelineRows(input.timeline),
    ].filter((r): r is AttentionRow => r !== null);

    const dated = [...datedTaskRows(input.tracker, input.today, raised), ...scheduleRows(input.timeline, input.nextScanAt, input.today)]
        .sort((a, b) => (a.day ?? "").localeCompare(b.day ?? ""));

    return [
        ...problems.filter((r) => r.tone === "amber"),
        ...problems.filter((r) => r.tone === "gray"),
        ...dated,
    ];
}


/* ------------------------------------------------------------------------- *
 * What to do next, from Delivery
 * ------------------------------------------------------------------------- */

/**
 * What to do next when Delivery is on: each deliverable's one next step, by
 * the rule every screen uses, joined with what the client started (a change,
 * a call) from `base`, the tracker-free rows `whatNext` still makes. A row
 * opens that deliverable's drawer on Delivery.
 */
/**
 * One row per piece of work, not per outlet. A plan with 2 or more outlets
 * carries S2 (the GBP Pack) once per outlet, and the step text has no outlet
 * name, so a 4-outlet client read "Create the GBP Pack" four times (Bee Choo,
 * 23 Oct 2026). Rows with the same code, month, step and status (due date included) fold into
 * "Create the GBP Pack, 4 outlets", which opens the first on Delivery, where
 * each outlet is listed. Order is kept: a fold sits where its first row was.
 */
export function outletRows(ds: Deliverable[], row: (d: Deliverable) => NextRow): NextRow[] {
    const groups = new Map<string, { row: NextRow; count: number }>();
    const order: string[] = [];
    for (const d of ds) {
        const r = row(d);
        // The whole status, due date included: outlets due on different days stay separate rows.
        const id = d.outlet ? JSON.stringify([d.code, d.month, r.title, r.status]) : JSON.stringify(["one", d.key]);
        const seen = groups.get(id);
        if (seen) seen.count += 1;
        else {
            groups.set(id, { row: r, count: 1 });
            order.push(id);
        }
    }
    return order.map((id) => {
        const { row: r, count } = groups.get(id)!;
        return count > 1 ? { ...r, title: `${r.title}, ${count} outlets` } : r;
    });
}

export function nextFromDelivery(payload: DeliveryPayload, base: NextWork, today: string): NextWork {
    const open = payload.deliverables.filter((d) => d.group === "needs_us" || d.group === "waiting_client" || d.group === "checking");
    const planIndex = new Map(payload.deliverables.map((d, i): [string, number] => [d.key, i]));
    const rankOf = (d: Deliverable) => todayRank(d, planIndex.get(d.key) ?? 99);
    const statusOf = (d: Deliverable): NextStatus => {
        if (d.actor?.cameBack) return { kind: "came_back" };
        if (d.actor?.late) return { kind: "late", days: d.due ? Math.max(1, workingDaysBetween(d.due, today)) : 1 };
        if (d.clientWord === "Not started") return { kind: "not_started", due: d.due };
        return { kind: "in_progress", due: d.due };
    };
    // Delivery's own order (came back, a client reply, due soonest, plan order), never re-sorted here.
    const ours = payload.deliverables.filter((d) => d.group === "needs_us" && d.next).sort((a, b) => rankOf(a) - rankOf(b));
    const usRow = (d: Deliverable): NextRow => ({ key: `delivery:${d.key}`, title: d.next!.text, status: statusOf(d), asked: false, link: { view: "delivery", deliverable: d.key }, code: d.code });
    // What the client started goes after the rows that lead the rule (came back, a client
    // reply, late), before the rest, so the order matches Delivery and Today.
    const leads = (d: Deliverable) => {
        const it = d.items.find((i) => i.id === d.next?.itemId);
        return Boolean(it?.cameBack || it?.clientRepliedAt || d.actor?.cameBack || d.actor?.late);
    };
    let cut = 0;
    while (cut < ours.length && leads(ours[cut])) cut += 1;
    const urgent = outletRows(ours.slice(0, cut), usRow);
    const rest = outletRows(ours.slice(cut), usRow);

    // The client's column: each document wait on the client once, with its question
    // (H13), then deliverables waiting on the client outside a document.
    const waits = (payload.documentWaits ?? []).filter((w) => w.waitingOn === "client" && w.deliverableKeys.length > 0);
    const inWait = new Set(waits.reduce<string[]>((all, w) => all.concat(w.deliverableKeys), []));
    const waitRows: NextRow[] = waits.map((w) => ({
        key: `delivery-doc:${w.docId ?? w.title}`,
        title: w.question ?? w.title,
        status: w.late ? { kind: "late", days: Math.max(1, w.lateWorkingDays ?? 1) } : { kind: "waiting" },
        asked: true,
        link: { view: "delivery", deliverable: w.deliverableKeys[0] },
        code: w.codes[0],
    }));
    const client: NextRow[] = outletRows(
        payload.deliverables.filter((d) => d.group === "waiting_client" && !inWait.has(d.key)).sort((a, b) => rankOf(a) - rankOf(b)),
        (d) => {
            const it = d.items.find((i) => i.state === "waiting" && i.waitingOn === "client");
            return { key: `delivery:${d.key}`, title: it?.nextText ?? d.label, status: { kind: "client_turn" }, asked: true, link: { view: "delivery", deliverable: d.key }, code: d.code };
        },
    );
    // Late first across every source (a document question, an ask on Client), each in its own order.
    const clientAll = [...waitRows, ...client, ...base.client];
    return {
        us: [...urgent, ...base.us, ...rest],
        client: [...clientAll.filter(isLate), ...clientAll.filter((r) => !isLate(r))],
        open: open.length,
    };
}

/** Needs attention rows Delivery adds: a late document not sent yet, and unconfirmed key pages. */
export function deliveryAttention(payload: DeliveryPayload): AttentionRow[] {
    // A fix that came back and a deliverable past its date are What to do next's
    // own rows (Came back, N days late), so they are not raised here as well.
    const rows: AttentionRow[] = [];
    // A document the catalogue expects that is late and not sent (H13). A late
    // question already raises "Question N days late" from the client's column.
    (payload.documentWaits ?? [])
        .filter((w) => w.late && w.stateKey === "not_sent_yet")
        .forEach((w) => {
            rows.push({
                key: `delivery:not-sent:${w.title}:${w.deliverableKeys[0] ?? ""}`,
                tone: "amber",
                title: `${w.title} not sent${w.expectedBy ? `, due ${dayLabel(w.expectedBy)}` : ""}`,
                sub: w.codes.join(", "),
                link: w.deliverableKeys[0] ? { view: "delivery", deliverable: w.deliverableKeys[0] } : { view: "delivery" },
            });
        });
    if (!payload.keyPages.confirmed) {
        rows.push({ key: "delivery:key-pages", tone: "gray", title: "The 10 key pages are not confirmed", sub: "Oura suggested them; staff confirm or swap any", link: { view: "delivery" } });
    }
    return rows;
}
